import { useDispatch } from 'react-redux';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { OnboardingRequester, type UpdateUserOnboarding, type UserOnboarding } from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';

import Alert from '@cobliteam/ui/components/Alert';

import { QUERY_KEY, QUERY_KEY_USER_DATA } from '../useUser';

const env = Environment.getInstance().getEnvironment();
const onboardingRequester = new OnboardingRequester({ host: env.apiHost });

const useOnboarding = () => {
  const queryClient = useQueryClient();

  const useSaveUserOnboarding = () => {
    const dispatch = useDispatch();

    return useMutation<UserOnboarding | undefined, unknown, UpdateUserOnboarding>({
      mutationFn: async (userOnboarding) => onboardingRequester.updateOnboarding(userOnboarding),
      onError: () => Alert.error('Falha ao salvar o onboarding. Tente novamente mais tarde.'),
      onSuccess: () =>
        queryClient.refetchQueries({
          queryKey: [QUERY_KEY, QUERY_KEY_USER_DATA],
        }),
    });
  };

  const useCreateUserOnboarding = () => {
    const dispatch = useDispatch();

    return useMutation<UserOnboarding | undefined>({
      mutationFn: async () => onboardingRequester.createOnboarding(),
      onError: () => Alert.error('Falha ao criar o onboarding. Tente novamente mais tarde.'),
      onSuccess: () =>
        queryClient.refetchQueries({
          queryKey: [QUERY_KEY, QUERY_KEY_USER_DATA],
        }),
    });
  };

  const useResetUserOnboarding = () => {
    const dispatch = useDispatch();

    return useMutation<UserOnboarding | undefined>({
      mutationFn: async () => onboardingRequester.resetOnboarding(),
      onError: () => Alert.error('Falha ao resetar o onboarding. Tente novamente mais tarde.'),
      onSuccess: () =>
        queryClient.refetchQueries({
          queryKey: [QUERY_KEY, QUERY_KEY_USER_DATA],
        }),
    });
  };

  return {
    useSaveUserOnboarding,
    useCreateUserOnboarding,
    useResetUserOnboarding,
  };
};

export default useOnboarding;
